import { useDispatch } from "react-redux";
import AttributeListItem from "./AttributeListItem";
import AttributeListingSubHeader from "./AttributeListingSubHeader";
import { useSelector } from "react-redux";
import { GET_ATTRIBUTE_DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { useEffect, useRef, useState } from "react";
import queryString from "query-string";
import { getAttributes } from "../../../core/actionCreators/attributeActionCreator";
import CommonPagination from "../CommonPagination/CommonPagination";
import Loader from "../Loader/Loader";
import { removeEmptyStringKeys } from "../../../utils/helper";

const EMPTY_FILTERS = {
  title: "",
  category: "",
  isActive: "",
};

const FILTER_DEBOUNCE_MS = 600;

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const AttributeList = () => {
  const dispatch = useDispatch();
  const { attributes, isLoading } = useSelector((s: any) => s.attributeStore);

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any);
  const [pageNumber, setPageNumber] = useState(0);

  // Typing should settle before firing a request; paging should not wait.
  const isFilterChangeRef = useRef(false);

  useEffect(() => {
    const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
    const timeout = setTimeout(() => {
      isFilterChangeRef.current = false;
      dispatch(getAttributes(
        queryString.stringify(removeEmptyStringKeys({
          ...filters,
          pageNumber,
          pageSize: GET_ATTRIBUTE_DEFAULT_PAGE_SIZE,
        })),
        null
      ));
    }, delay);
    return () => clearTimeout(timeout);
  }, [filters, pageNumber, dispatch]);

  // A changed filter re-runs the search from the first page — keeping the old
  // page number would land the user on an empty tail of a shorter result set.
  const onFilterChange = (key: string, value: string) => {
    isFilterChangeRef.current = true;
    setFilters({ ...filters, [key]: value });
    setPageNumber(0);
  };

  const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key]);

  const clearFilters = () => {
    isFilterChangeRef.current = false;
    setFilters({ ...EMPTY_FILTERS });
    setPageNumber(0);
  };

  // CommonPagination counts pages from 1, the API counts them from 0.
  const onPageChange = (page: any) => {
    setPageNumber(Math.max(0, Number(page) - 1));
  };

  return (
    <>
      <AttributeListingSubHeader category={filters.category} />
      <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="w-[280px]">
          <label className="block text-[11px] text-gray-600 mb-1">Title</label>
          <input
            type="text"
            placeholder="Search title"
            value={filters.title}
            onChange={(e) => onFilterChange("title", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="w-[180px]">
          <label className="block text-[11px] text-gray-600 mb-1">Category</label>
          <input
            type="text"
            placeholder="Category code"
            value={filters.category}
            onChange={(e) => onFilterChange("category", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="w-[140px]">
          <label className="block text-[11px] text-gray-600 mb-1">Status</label>
          <select
            value={filters.isActive}
            onChange={(e) => onFilterChange("isActive", e.target.value)}
            className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <button
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear Filters
        </button>
      </div>
      <div className="mt-2.5 campaign-list bg-white mb-[54px]">
        <table className="w-full">
          <thead className="bg-[#343434]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[8%]">Sno</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[34%]">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[18%]">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[14%]">Product Count</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[14%]">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[12%]">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-12">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Loader />
                    <span className="text-xs text-gray-500">Loading attributes...</span>
                  </div>
                </td>
              </tr>
            ) : (
              <AttributeListItem data={attributes?.data || []} />
            )}
          </tbody>
        </table>
      </div>
      {attributes.data && (
        <CommonPagination
          data={attributes}
          PAGE_SIZE={GET_ATTRIBUTE_DEFAULT_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};
export default AttributeList;
